/**
 * Kind Cotton — Book Donation Selector
 * Trello 1173
 *
 * - Tile click / hover / tap opens action panel for that option
 * - Give Monthly adds the configured variant with its selling plan
 * - One-Time adds the configured variant with no selling plan
 * - Missing selling plan on Give Monthly fails hard (no silent fallback)
 * - On success, the store's installed cart-drawer extension (HotSlide Cart
 *   Drawer / "white background cart") auto-detects /cart/add.js completion
 *   and opens itself — we don't dispatch any open event ourselves.
 * - While adding, shows a spinner overlay on the selected book stack.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-bd-root]';
  var OPTION_SELECTOR = '[data-bd-option]';
  var TILE_SELECTOR = '[data-bd-tile]';
  var ACTION_SELECTOR = '[data-bd-action]';
  var ACTIVE_CLASS = 'is-active';
  var BUSY_CLASS = 'is-busy';

  function isCoarsePointer() {
    return window.matchMedia && window.matchMedia('(hover: none)').matches;
  }

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function closeAll(root, except) {
    root.querySelectorAll(OPTION_SELECTOR).forEach(function (opt) {
      if (opt !== except) {
        opt.classList.remove(ACTIVE_CLASS);
        var tile = opt.querySelector(TILE_SELECTOR);
        if (tile) tile.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function openOption(root, option) {
    closeAll(root, option);
    option.classList.add(ACTIVE_CLASS);
    var tile = option.querySelector(TILE_SELECTOR);
    if (tile) tile.setAttribute('aria-expanded', 'true');
  }

  function showError(root, message) {
    var el = root.querySelector('[data-bd-status]');
    if (!el) return;
    el.textContent = message || '';
    el.classList.add('bd-status--error');
  }

  function clearError(root) {
    var el = root.querySelector('[data-bd-status]');
    if (!el) return;
    el.textContent = '';
    el.classList.remove('bd-status--error');
  }

  function addToCart(payload) {
    return fetch(shopifyRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    });
  }

  function handleAction(root, option, mode) {
    if (option.classList.contains(BUSY_CLASS)) return;

    var variantId = option.getAttribute('data-variant-id');
    var sellingPlanId = option.getAttribute('data-selling-plan-id');
    var productTitle = option.getAttribute('data-product-title') || 'this option';

    if (!variantId) {
      showError(root, 'This option is not configured yet. Please contact us.');
      return;
    }

    var payload = {
      items: [{ id: parseInt(variantId, 10), quantity: 1 }]
    };

    if (mode === 'monthly') {
      if (!sellingPlanId) {
        showError(
          root,
          'Monthly giving is not available for ' + productTitle + ' yet. Please try One-Time, or contact us.'
        );
        return;
      }
      payload.items[0].selling_plan = parseInt(sellingPlanId, 10);
    }

    clearError(root);
    option.classList.add(BUSY_CLASS);

    addToCart(payload)
      .then(function (resp) {
        if (!resp.ok) {
          var msg =
            (resp.data && (resp.data.description || resp.data.message)) ||
            'Could not add to cart (HTTP ' + resp.status + ').';
          showError(root, msg);
          option.classList.remove(BUSY_CLASS);
          return;
        }
        // The store's cart-drawer extension auto-opens on /cart/add.js
        // completion. Just stop the spinner shortly after to give the
        // drawer a beat to render before we drop the active state.
        setTimeout(function () {
          option.classList.remove(BUSY_CLASS);
          option.classList.remove(ACTIVE_CLASS);
        }, 700);
      })
      .catch(function (err) {
        showError(root, 'Network error: ' + (err && err.message ? err.message : 'unknown'));
        option.classList.remove(BUSY_CLASS);
      });
  }

  function bindRoot(root) {
    if (root.dataset.bdBound === '1') return;
    root.dataset.bdBound = '1';

    var coarse = isCoarsePointer();

    root.querySelectorAll(OPTION_SELECTOR).forEach(function (option) {
      var tile = option.querySelector(TILE_SELECTOR);
      if (!tile) return;

      var configured = option.getAttribute('data-product-configured') === 'true';
      var variantId = option.getAttribute('data-variant-id');

      tile.addEventListener('click', function (e) {
        var target = e.target;
        var actionEl = target.closest && target.closest(ACTION_SELECTOR);

        if (actionEl) {
          e.preventDefault();
          e.stopPropagation();
          if (!configured || !variantId) {
            showError(root, 'This option is not configured yet.');
            return;
          }
          handleAction(root, option, actionEl.getAttribute('data-bd-action'));
          return;
        }

        if (!configured || !variantId) return;

        if (option.classList.contains(ACTIVE_CLASS)) {
          if (coarse) {
            option.classList.remove(ACTIVE_CLASS);
            tile.setAttribute('aria-expanded', 'false');
          }
          return;
        }
        openOption(root, option);
      });

      if (!coarse) {
        option.addEventListener('mouseenter', function () {
          if (!configured || !variantId) return;
          openOption(root, option);
        });
      }

      tile.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          option.classList.remove(ACTIVE_CLASS);
          tile.setAttribute('aria-expanded', 'false');
          tile.focus();
        }
      });
    });

    if (!coarse) {
      root.addEventListener('mouseleave', function () {
        // Don't auto-close while busy — keep spinner visible on the selected tile.
        var anyBusy = root.querySelector('.bd-option.' + BUSY_CLASS);
        if (!anyBusy) closeAll(root, null);
      });
    } else {
      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) {
          var anyBusy = root.querySelector('.bd-option.' + BUSY_CLASS);
          if (!anyBusy) closeAll(root, null);
        }
      });
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector) {
      e.target.querySelectorAll(ROOT_SELECTOR).forEach(bindRoot);
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    if (e.target && e.target.querySelector) {
      e.target.querySelectorAll(ROOT_SELECTOR).forEach(bindRoot);
    }
  });
})();
